import { useQuery } from '@tanstack/react-query'
import { AlarmClock, BookCopy, CircleDollarSign, Library } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/States'
import { Alert } from '@/components/ui/Alert'
import { Avatar } from '@/components/ui/Avatar'
import { Card, CardHeader } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { StatCard } from '@/components/ui/StatCard'
import { useAuth } from '@/features/auth/useAuth'
import { childLibraryQuery } from '@/features/library/api'
import { LoanTable } from '@/features/library/components/LoanTable'
import { isPositiveAmount } from '@/features/library/status'
import { familyQuery } from '@/features/portal/api'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { cn } from '@/utils/cn'
import { formatMoney } from '@/utils/format'

function ChildLoans({ studentId, name }: { studentId: number; name: string }) {
  const { t } = useTranslation('library')
  const { school } = useAuth()
  const currency = school?.currency ?? 'USD'
  const { data, isPending, error, refetch } = useQuery(childLibraryQuery(studentId))
  if (isPending) return <LoadingState />
  if (error) return <ErrorState onRetry={() => void refetch()} />
  const b = data.borrower
  const open = data.loans.filter((l) => l.state !== 'returned')
  const history = data.loans.filter((l) => l.state === 'returned')
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard
          label={t('Books on loan')}
          value={`${b.activeLoans} / ${b.loanLimit}`}
          icon={<BookCopy />}
        />
        <StatCard
          label={t('Overdue')}
          value={b.overdueLoans}
          icon={<AlarmClock />}
          tone={b.overdueLoans ? 'danger' : 'success'}
        />
        <StatCard
          label={t('Unpaid fines')}
          value={formatMoney(b.finesOutstanding, currency)}
          icon={<CircleDollarSign />}
          tone="warning"
        />
      </div>
      {b.overdueLoans > 0 ? (
        <Alert tone="warning" title={t('Overdue books')}>
          {t('Please return overdue books to the library — a fine accrues for each day late.')}
        </Alert>
      ) : null}
      {isPositiveAmount(b.finesOutstanding) ? (
        <Alert tone="info">{t('Library fines can be settled at the library desk.')}</Alert>
      ) : null}
      <Card>
        <CardHeader title={t("{{name}}'s current loans", { name })} />
        <LoanTable
          caption={t('Current loans')}
          loans={open}
          currency={currency}
          showBorrower={false}
          empty={<EmptyState icon={<Library />} title={t('No books on loan')} compact />}
        />
      </Card>
      {history.length ? (
        <Card>
          <CardHeader title={t('Recently returned')} />
          <LoanTable caption={t('Loan history')} loans={history} currency={currency} showBorrower={false} />
        </Card>
      ) : null}
    </div>
  )
}

export function FamilyLibraryPage() {
  const { t } = useTranslation('library')
  useDocumentTitle('Library')
  const children = useQuery(familyQuery())
  const [selected, setSelected] = useState(0)
  if (children.isPending) return <LoadingState />
  if (children.error) return <ErrorState onRetry={() => void children.refetch()} />
  const child = children.data[selected] ?? children.data[0]
  return (
    <>
      <PageHeader title={t('Library')} description={t('Books borrowed, due dates and any fines.')} />
      {children.data.length > 1 ? (
        <div role="tablist" aria-label={t('Choose child')} className="mb-6 flex gap-2 overflow-x-auto">
          {children.data.map((c, i) => (
            <button
              key={c.student.id}
              role="tab"
              type="button"
              aria-selected={i === selected}
              onClick={() => setSelected(i)}
              className={cn(
                'flex shrink-0 items-center gap-2 rounded-full border py-1 pr-4 pl-1 text-sm font-medium transition-colors',
                i === selected
                  ? 'border-brand-300 bg-brand-50 text-brand-700'
                  : 'border-line bg-surface text-ink-soft hover:border-line-strong',
              )}
            >
              <Avatar name={c.student.fullName} src={c.student.photoUrl} size="sm" />
              {c.student.firstName}
            </button>
          ))}
        </div>
      ) : null}
      {child ? (
        <ChildLoans key={child.student.id} studentId={child.student.id} name={child.student.firstName} />
      ) : (
        <Card>
          <EmptyState
            icon={<Library />}
            title={t('No student linked')}
            description={t("Your account isn't linked to a student record yet.")}
          />
        </Card>
      )}
    </>
  )
}
